import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'

import { GET, httpOk } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'

const QUICK_FILTERS = [
	{ id: 'today', key: 'filter_today' },
	{ id: 'yesterday', key: 'filter_yesterday' },
	{ id: 'week', key: 'filter_7days' },
	{ id: 'month', key: 'filter_30days' },
]

function getDateRange(id) {
	const now = new Date()
	const fmt = d => formatDate(d, 'yyyy-MM-dd')
	if (id === 'today') return { start: fmt(now), end: fmt(now) }
	if (id === 'yesterday') {
		const d = new Date(now); d.setDate(d.getDate() - 1)
		return { start: fmt(d), end: fmt(d) }
	}
	if (id === 'week') {
		const d = new Date(now); d.setDate(d.getDate() - 6)
		return { start: fmt(d), end: fmt(now) }
	}
	const d = new Date(now); d.setDate(d.getDate() - 29)
	return { start: fmt(d), end: fmt(now) }
}

const mln = (v) => {
	v = Number(v) || 0
	const a = Math.abs(v)
	if (a >= 1e9) return (v / 1e9).toFixed(1) + ' mlrd'
	if (a >= 1e6) return (v / 1e6).toFixed(1) + ' mln'
	if (a >= 1e3) return Math.round(v / 1e3) + ' ming'
	return String(Math.round(v))
}

const CAT_COLORS = ['#e5556e', '#f2996a', '#eab308', '#4f79e8', '#22bfa9', '#8b5cf6', '#64748b', '#ec4899', '#10b981', '#f59e0b']

function Delta({ v }) {
	// Xarajat oshishi = yomon (qizil), kamayishi = yaxshi (yashil)
	if (!v) return <span className="x-delta x-flat">0%</span>
	return <span className={'x-delta ' + (v > 0 ? 'x-up' : 'x-dn')}>{v > 0 ? '▲' : '▼'} {Math.abs(v)}%</span>
}

function Spark({ vals }) {
	const max = Math.max(...vals, 1)
	const w = 100, h = 32
	const pts = vals.map((v, i) => `${(i / (vals.length - 1 || 1)) * w},${(h - (v / max) * h).toFixed(1)}`).join(' ')
	return (
		<svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height: 56, display: 'block' }}>
			<polyline points={pts} fill="none" stroke="#e5556e" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
		</svg>
	)
}

function ExpenseAnalysis() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [quick, setQuick] = useState('month')
	const [range, setRange] = useState(getDateRange('month'))
	const [custom, setCustom] = useState({ start: null, end: null })
	const [expanded, setExpanded] = useState(null)   // ochilgan modda id
	const [details, setDetails] = useState({})        // { modda_id: [operatsiyalar] }

	const load = useCallback(async (r) => {
		setExpanded(null); setDetails({})   // yangi davr — drill-down keshini tozalash
		const res = await GET('/analytics/expenses', { start_date: r.start, end_date: r.end }, { loader: true })
		if (httpOk(res)) setData(res.data)
	}, [])

	useEffect(() => { load(range) }, [range, load])

	// Moddani ochish/yopish — ichidagi operatsiyalarni ko'rsatish
	async function toggleCat(c) {
		if (!c.id) return
		if (expanded === c.id) { setExpanded(null); return }
		setExpanded(c.id)
		if (!details[c.id]) {
			const res = await GET('/expenses', {
				payment_purpose_id: c.id, transaction_type: 'minus',
				start_date: range.start, end_date: range.end, size: 200,
			}, { loader: true })
			if (httpOk(res)) setDetails(prev => ({ ...prev, [c.id]: res.data.data ?? [] }))
		}
	}

	function pickQuick(id) {
		setQuick(id); setCustom({ start: null, end: null }); setRange(getDateRange(id))
	}
	function applyCustom(start, end) {
		setCustom({ start, end })
		if (start && end) { setQuick(''); setRange({ start: formatDate(start, 'yyyy-MM-dd'), end: formatDate(end, 'yyyy-MM-dd') }) }
	}

	function exportCsv() {
		if (!d) return
		const rows = [['Modda', 'Summa', 'Ulush %', 'Operatsiya']]
		d.categories.forEach(c => rows.push([c.name, c.amt, c.share, c.cnt]))
		const csv = rows.map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(';')).join('\n')
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
		const a = document.createElement('a')
		a.href = URL.createObjectURL(blob)
		a.download = `xarajat_${d.period.start}_${d.period.end}.csv`
		a.click()
	}

	const d = data

	const smallList = (items) => {
		const total = items.reduce((s, i) => s + i.amt, 0) || 1
		return items.map((it, i) => (
			<div className="x-l2" key={i}>
				<span className="x-dot" style={{ background: CAT_COLORS[i % CAT_COLORS.length] }} />
				<span className="x-l2n">{it.name}</span>
				<span className="x-l2v">{mln(it.amt)}</span>
				<span className="x-l2p">{Math.round(it.amt / total * 100)}%</span>
			</div>
		))
	}

	return (
		<div className="exp-dash">
			<style>{EXP_CSS}</style>

			<div className="x-top">
				<div className="x-brand">
					<div className="x-mark">
						<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h18M3 18h12" /></svg>
					</div>
					<div>
						<h4 className="x-h1">{t('expense_dashboard')}</h4>
						<div className="x-sub">{t('one_branch')} · {t('sum')} · {d ? `${formatDate(d.period.start)} — ${formatDate(d.period.end)}` : '...'}</div>
					</div>
				</div>
				<div className="x-tools">
					<div className="x-chips">
						{QUICK_FILTERS.map(f => (
							<button key={f.id} className={'x-chip' + (quick === f.id ? ' on' : '')} onClick={() => pickQuick(f.id)}>{t(f.key)}</button>
						))}
						<span className="x-dp">
							<DatePicker selectsRange startDate={custom.start} endDate={custom.end} dateFormat="dd.MM.yyyy"
								onChange={([s, e]) => applyCustom(s, e)} placeholderText={t('select_date')} className="x-dp-input" isClearable />
						</span>
					</div>
					<button className="x-xls" onClick={exportCsv} disabled={!d}>
						<i className="uil-file-download-alt" /> Excel
					</button>
				</div>
			</div>

			{!d ? <div className="x-empty">{t('loading')}</div> : (
				<div className="x-grid">

					{/* KPI */}
					<div className="x-card x-kpi hero c3">
						<div className="x-lbl">{t('exp_total')}</div>
						<div className="x-big exp">{mln(d.kpi.total)}</div>
						<Delta v={d.kpi.total_delta} />
						<div className="x-foot">{formatMoney(d.kpi.total)} {t('sum')}</div>
					</div>
					<div className="x-card x-kpi c3">
						<div className="x-lbl">{t('operations')}</div>
						<div className="x-big">{d.kpi.count}</div>
						<Delta v={d.kpi.count_delta} />
						<div className="x-foot">{t('expense_records')}</div>
					</div>
					<div className="x-card x-kpi c3">
						<div className="x-lbl">{t('exp_avg')}</div>
						<div className="x-big">{mln(d.kpi.avg)}</div>
						<div className="x-foot">{t('per_operation')}</div>
					</div>
					<div className="x-card x-kpi c3 x-top1">
						<div className="x-lbl">{t('top_category')}</div>
						<div className="x-t1n" title={d.kpi.top_name}>{d.kpi.top_name}</div>
						<div className="x-t1bar"><i style={{ width: d.kpi.top_share + '%' }} /></div>
						<div className="x-t1row"><span style={{ fontWeight: 800 }}>{mln(d.kpi.top_amt)}</span><span className="x-t1pct">{d.kpi.top_share}%</span></div>
					</div>

					{/* Qaysi xarajat turi eng ko'p — ochiladigan */}
					<div className="x-card c12">
						<div className="x-hd"><span className="x-ic">▦</span>{t('which_category_most')}<span className="x-r">{t('click_to_expand')}</span></div>
						<div className="x-mlist">
							{d.categories.length === 0 && <div className="x-foot">{t('no_data')}</div>}
							{d.categories.map((c, i) => (
								<div key={i}>
									<div className={'x-mrow' + (c.id ? ' x-click' : '') + (expanded === c.id ? ' x-open' : '')} onClick={() => toggleCat(c)}>
										<span className="x-mn">
											{c.id && <i className={'x-caret uil-angle-' + (expanded === c.id ? 'down' : 'right')} />}
											<span className="x-sw" style={{ background: CAT_COLORS[i % CAT_COLORS.length] }} />{c.name}
										</span>
										<span className="x-mv">{mln(c.amt)}</span>
										<div className="x-track"><i style={{ width: Math.max(2, c.share) + '%', background: CAT_COLORS[i % CAT_COLORS.length] }} /></div>
										<div className="x-meta"><span>{t('share_word')} <b>{c.share}%</b></span><span>{t('operation_word')} <b>{c.cnt}</b></span></div>
									</div>
									{expanded === c.id && (
										<div className="x-drill">
											{!details[c.id] ? <div className="x-foot" style={{ padding: '8px 0' }}>{t('loading')}</div> :
												details[c.id].length === 0 ? <div className="x-foot" style={{ padding: '8px 0' }}>{t('no_records_inside')}</div> :
													details[c.id].map((op, j) => (
														<div className="x-drow" key={j}>
															<span className="x-dt">{formatDate(op.created_at, 'dd.MM.yyyy')}</span>
															<span className="x-dnote" title={op.note}>{op.note || '—'}</span>
															{op.payment_name && <span className="x-pill">{op.payment_name}</span>}
															<span className="x-da">{mln(op.amount)}</span>
														</div>
													))}
										</div>
									)}
								</div>
							))}
						</div>
					</div>

					{/* Daromadga nisbatan */}
					<div className="x-card c6">
						<div className="x-hd"><span className="x-ic">◑</span>{t('vs_revenue')}</div>
						<div className="x-ratio">
							<div className="x-ring" style={{ background: `conic-gradient(#e5556e ${Math.min(100, d.ratio)}%, var(--x-ins) 0)` }}>
								<div className="x-ring-in"><b>{d.ratio}%</b><span>{t('of_sales')}</span></div>
							</div>
							<div className="x-rx">
								{t('kpi_sales')}: <b>{mln(d.revenue)}</b><br />
								{t('lbl_expense')}: <b style={{ color: 'var(--x-exp)' }}>{mln(d.kpi.total)}</b><br />
								{t('net_balance')}: <b style={{ color: d.net < 0 ? 'var(--x-exp)' : 'var(--x-up)' }}>{mln(d.net)}</b>
							</div>
						</div>
						<div className="x-foot" style={{ marginTop: 14 }}>
							<b style={{ color: 'var(--x-exp)' }}>{d.ratio}%</b> — {t('exp_ratio_hint')}
						</div>
					</div>

					{/* To'lov turi bo'yicha */}
					<div className="x-card c6">
						<div className="x-hd"><span className="x-ic">◈</span>{t('by_payment_type')}</div>
						<div className="x-list2">{d.by_paytype.length ? smallList(d.by_paytype) : <div className="x-foot">{t('no_data')}</div>}</div>
					</div>

					{/* Kassa bo'yicha */}
					<div className="x-card c6">
						<div className="x-hd"><span className="x-ic">▤</span>{t('exp_by_account')}</div>
						<div className="x-list2">{d.by_account?.length ? smallList(d.by_account) : <div className="x-foot">{t('no_data')}</div>}</div>
					</div>

					{/* Kassir bo'yicha */}
					<div className="x-card c6">
						<div className="x-hd"><span className="x-ic">◕</span>{t('exp_by_cashier')}</div>
						<div className="x-list2">{d.by_cashier?.length ? smallList(d.by_cashier) : <div className="x-foot">{t('no_data')}</div>}</div>
					</div>

					{/* USD taminotchi (som jamidan alohida) */}
					{d.usd_suppliers?.total > 0 && (
						<div className="x-card c6">
							<div className="x-hd"><span className="x-ic">$</span>{t('exp_usd_suppliers')}</div>
							<div className="x-big" style={{ fontSize: 22, fontWeight: 800 }}>{formatMoney(d.usd_suppliers.total)} <span style={{ fontSize: 13, color: 'var(--x-mut)' }}>$</span></div>
							<div className="x-foot">{d.usd_suppliers.cnt} {t('operations')} · USD</div>
						</div>
					)}

					{/* Dinamika */}
					{d.trend?.amounts?.length > 1 && (
						<div className="x-card c6">
							<div className="x-hd"><span className="x-ic">◠</span>{t('exp_trend')}</div>
							<Spark vals={d.trend.amounts} />
						</div>
					)}

					{/* So'nggi xarajatlar */}
					{d.recent?.length > 0 && (
						<div className="x-card c12">
							<div className="x-hd"><span className="x-ic">▦</span>{t('exp_recent')}</div>
							<div className="x-tbl-wrap">
								<table className="x-tbl">
									<thead><tr><th>{t('date')}</th><th>{t('lbl_expense')}</th><th>{t('note')}</th><th className="r">{t('amount')}</th></tr></thead>
									<tbody>
										{d.recent.map((r, i) => (
											<tr key={i}>
												<td className="x-nowrap x-mut">{formatDate(r.date, 'dd.MM.yyyy')}</td>
												<td className="x-cat">{r.modda}</td>
												<td className="x-ell" title={r.note}>{r.note || '—'}</td>
												<td className="r x-amt">{formatMoney(r.amt)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}

				</div>
			)}
		</div>
	)
}

const EXP_CSS = `
.exp-dash{--x-exp:#e5556e;--x-exp2:#f2996a;--x-blue:#4f79e8;--x-up:#1fae74;--x-ink:#1c2a38;--x-mut:#6b7c8d;--x-fnt:#9aa9b8;--x-line:#e6ecf3;--x-line2:#eef2f7;--x-ins:#f2f5fa;--x-pan:#fff;font-size:14px;color:var(--x-ink)}
.x-top{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:18px}
.x-brand{display:flex;align-items:center;gap:12px}
.x-mark{width:40px;height:40px;border-radius:10px;background:linear-gradient(150deg,var(--x-exp),var(--x-exp2));display:grid;place-items:center;flex:none}
.x-mark svg{width:22px;height:22px}
.x-h1{margin:0;font-size:19px;font-weight:800;letter-spacing:-.2px}
.x-sub{color:var(--x-mut);font-size:12.5px;font-weight:500;margin-top:1px}
.x-tools{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.x-chips{display:flex;gap:5px;align-items:center;flex-wrap:wrap;background:var(--x-pan);padding:5px;border-radius:11px;border:1px solid var(--x-line)}
.x-chip{border:0;background:transparent;color:var(--x-ink);font:inherit;font-size:12.5px;font-weight:600;padding:7px 12px;border-radius:8px;cursor:pointer}
.x-chip.on{background:var(--x-exp);color:#fff}
.x-dp-input{border:1px solid var(--x-line);border-radius:8px;padding:7px 10px;font-size:12.5px;width:140px}
.x-xls{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--x-line);background:var(--x-pan);color:var(--x-up);font-weight:700;font-size:12.5px;padding:8px 13px;border-radius:10px;cursor:pointer}
.x-xls:disabled{opacity:.5;cursor:default}
.x-empty{padding:40px;text-align:center;color:var(--x-mut)}
.x-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:13px}
.x-card{background:var(--x-pan);border:1px solid var(--x-line);border-radius:13px;box-shadow:0 1px 2px rgba(16,34,46,.04),0 4px 16px rgba(16,34,46,.04);padding:15px 16px}
.c3{grid-column:span 3}.c4{grid-column:span 4}.c5{grid-column:span 5}.c6{grid-column:span 6}.c7{grid-column:span 7}.c8{grid-column:span 8}.c12{grid-column:1/-1}
@media(max-width:1000px){.c3,.c4,.c5,.c6,.c7,.c8{grid-column:span 6}}
@media(max-width:560px){.x-grid{grid-template-columns:repeat(2,1fr)}.c3,.c4,.c5,.c6,.c7,.c8,.c12{grid-column:span 2}}
.x-lbl{font-size:10.5px;letter-spacing:.09em;text-transform:uppercase;color:var(--x-mut);font-weight:700}
.x-hd{display:flex;align-items:center;gap:8px;margin-bottom:13px;font-size:13px;font-weight:750}
.x-ic{color:var(--x-exp)}
.x-r{margin-left:auto;font-size:11px;color:var(--x-fnt);font-weight:600;text-transform:none;letter-spacing:0}
.x-kpi .x-big{font-size:24px;font-weight:820;letter-spacing:-.5px;margin:8px 0 4px;line-height:1;font-variant-numeric:tabular-nums}
.x-big.exp{color:var(--x-exp)}
.x-foot{margin-top:6px;font-size:11.5px;color:var(--x-fnt);font-weight:500;font-variant-numeric:tabular-nums}
.x-kpi.hero{position:relative;overflow:hidden}
.x-kpi.hero::before{content:"";position:absolute;left:0;top:0;bottom:0;width:4px;background:linear-gradient(var(--x-exp),var(--x-exp2))}
.x-delta{display:inline-flex;align-items:center;gap:3px;font-size:11.5px;font-weight:700;padding:2px 7px;border-radius:20px}
.x-up{color:var(--x-exp);background:rgba(229,85,114,.12)}.x-dn{color:var(--x-up);background:rgba(31,174,116,.12)}.x-flat{color:var(--x-fnt);background:var(--x-ins)}
.x-top1 .x-t1n{font-size:15px;font-weight:800;letter-spacing:-.2px;margin:8px 0 9px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.x-t1bar{height:10px;border-radius:6px;background:var(--x-ins);overflow:hidden;margin-bottom:7px}
.x-t1bar>i{display:block;height:100%;border-radius:6px;background:linear-gradient(90deg,var(--x-exp),var(--x-exp2))}
.x-t1row{display:flex;justify-content:space-between;align-items:baseline;font-variant-numeric:tabular-nums}
.x-t1pct{font-size:14px;font-weight:800;color:var(--x-exp)}
.x-mlist{display:flex;flex-direction:column;gap:12px}
.x-mrow{display:grid;grid-template-columns:1fr auto;gap:4px 10px;align-items:center}
.x-mn{font-weight:700;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.x-sw{display:inline-block;width:9px;height:9px;border-radius:3px;margin-right:7px;vertical-align:-1px}
.x-mv{font-weight:800;font-size:14px;font-variant-numeric:tabular-nums}
.x-track{grid-column:1/-1;height:9px;border-radius:6px;background:var(--x-ins);overflow:hidden}
.x-track>i{display:block;height:100%;border-radius:6px}
.x-meta{grid-column:1/-1;display:flex;gap:14px;font-size:11.5px;color:var(--x-mut);font-weight:600}
.x-meta b{color:#33475b}
.x-ratio{display:flex;align-items:center;gap:16px}
.x-ring{width:92px;height:92px;flex:none;border-radius:50%;display:grid;place-items:center}
.x-ring-in{width:70px;height:70px;border-radius:50%;background:var(--x-pan);display:flex;flex-direction:column;align-items:center;justify-content:center}
.x-ring-in b{font-size:21px;font-weight:820;line-height:1}
.x-ring-in span{font-size:9px;letter-spacing:.05em;text-transform:uppercase;color:var(--x-mut);font-weight:700}
.x-rx{font-size:12.5px;color:#33475b;font-weight:600;line-height:1.7;font-variant-numeric:tabular-nums}
.x-rx b{font-weight:800}
.x-list2{display:flex;flex-direction:column}
.x-l2{display:flex;align-items:center;gap:9px;padding:8px 0;border-bottom:1px solid var(--x-line2)}
.x-l2:last-child{border-bottom:0}
.x-dot{width:9px;height:9px;border-radius:3px;flex:none}
.x-l2n{font-weight:650;font-size:12.5px;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.x-l2v{font-weight:800;font-size:12.5px;font-variant-numeric:tabular-nums}
.x-l2p{font-size:11px;color:var(--x-fnt);font-weight:600;width:40px;text-align:right;font-variant-numeric:tabular-nums}
.x-tbl-wrap{overflow-x:auto}
.x-tbl{width:100%;border-collapse:collapse;font-size:12.5px;min-width:760px}
.x-tbl th{text-align:left;font-size:10.5px;letter-spacing:.04em;text-transform:uppercase;color:var(--x-mut);font-weight:700;padding:0 10px 9px;border-bottom:1px solid var(--x-line)}
.x-tbl td{padding:9px 10px;border-bottom:1px solid var(--x-line2)}
.x-tbl tbody tr:last-child td{border-bottom:0}
.x-tbl .r{text-align:right}
.x-amt{font-weight:800;color:var(--x-exp);font-variant-numeric:tabular-nums}
.x-cat{font-weight:650}
.x-mut{color:var(--x-fnt)}
.x-nowrap{white-space:nowrap;font-variant-numeric:tabular-nums}
.x-ell{max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.x-pill{display:inline-block;font-size:11px;font-weight:700;padding:2px 8px;border-radius:20px;background:var(--x-ins);color:#33475b}
.x-mrow.x-click{cursor:pointer;border-radius:9px;transition:background .12s;padding:6px;margin:0 -6px}
.x-mrow.x-click:hover{background:var(--x-ins)}
.x-mrow.x-open{background:var(--x-ins)}
.x-caret{color:var(--x-mut);font-size:16px;margin-right:3px;vertical-align:-3px}
.x-drill{margin:2px 0 12px 26px;border-left:2px solid var(--x-line);padding-left:13px}
.x-drow{display:flex;align-items:center;gap:11px;padding:7px 0;border-bottom:1px solid var(--x-line2);font-size:12.5px}
.x-drow:last-child{border-bottom:0}
.x-dt{color:var(--x-mut);font-weight:600;white-space:nowrap;font-variant-numeric:tabular-nums}
.x-dnote{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#33475b}
.x-da{font-weight:800;color:var(--x-exp);white-space:nowrap;font-variant-numeric:tabular-nums}
`

export default ExpenseAnalysis
